import React, { useState } from 'react';
import { X, Image as ImageIcon, Plus } from 'lucide-react';

const EFFECT_TYPES = ["Waterfall", "Mine", "Gerb", "Spark", "Flame", "Comet", "Strobe", "Flash", "Confetti", "Other"];

const EffectSelectionModal = ({ effects, onSelectEffect, onClose }) => {
  const [filterType, setFilterType] = useState('');
  const [selectedEffect, setSelectedEffect] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const filteredEffects = effects.filter(effect => 
    !filterType || effect.type === filterType
  );

  const handleSelectEffect = (effect) => {
    setSelectedEffect(effect);
    setSelectedVariant(null); // Reset variant selection when changing effect
    
    // Parse variants if they exist
    if (effect.variants) {
      try {
        const variants = JSON.parse(effect.variants);
        if (variants.length > 0) {
          setSelectedVariant(variants[0].name); // Default to first variant
        }
      } catch (e) {
        console.error('Error parsing variants:', e);
      }
    }
  };

  const handleConfirmSelection = () => {
    if (selectedEffect) {
      onSelectEffect(selectedEffect.id, quantity, selectedVariant);
    }
  };

  const getEffectVariants = (effect) => {
    if (!effect.variants) return [];
    try {
      return JSON.parse(effect.variants);
    } catch (e) {
      console.error('Error parsing variants:', e);
      return [];
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '700px', maxHeight: '80vh' }}>
        <div className="modal-header">
          <h2>Add Effect to Event</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '0 20px 20px' }}>
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label>Filter by Type</label>
            <select
              className="form-control"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Types</option>
              {EFFECT_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div style={{ 
            maxHeight: '500px', 
            overflowY: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}>
            {filteredEffects.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                <p>No effects found.</p>
                {filterType && <p>Try changing the filter or add new effects in the Effects Database.</p>}
              </div>
            ) : (
              filteredEffects.map((effect) => (
                <div
                  key={effect.id}
                  style={{
                    padding: '15px',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    backgroundColor: 'white',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  {/* Effect Image */}
                  <div style={{ flexShrink: 0 }}>
                    {effect.image_data ? (
                      <img
                        src={`data:image/png;base64,${effect.image_data}`}
                        alt={effect.name}
                        style={{ 
                          width: '60px', 
                          height: '60px', 
                          borderRadius: '4px',
                          objectFit: 'cover',
                          border: '1px solid #ddd'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '60px', 
                        height: '60px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <ImageIcon size={24} style={{ color: '#666' }} />
                      </div>
                    )}
                  </div>

                  {/* Effect Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', marginBottom: '5px', fontSize: '16px' }}>
                      {effect.name}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                      <strong>Type:</strong> {effect.type} • <strong>Category:</strong> {effect.category || 'N/A'}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                      <strong>Range:</strong> {effect.effective_range || 'N/A'}m • <strong>Safety:</strong> {effect.safety_distance || 'N/A'}m
                    </div>
                    {effect.hazard_class && (
                      <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                        <strong>Hazard Class:</strong> {effect.hazard_class}
                      </div>
                    )}
                    {effect.description && (
                      <div style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>
                        {effect.description.length > 120 
                          ? effect.description.substring(0, 120) + '...'
                          : effect.description
                        }
                      </div>
                    )}
                  </div>

                  {/* Add Button */}
                  <div style={{ flexShrink: 0 }}>
                    <button
                      className={`btn ${selectedEffect?.id === effect.id ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => handleSelectEffect(effect)}
                      style={{ padding: '8px 12px' }}
                    >
                      <Plus size={16} className="mr-2" />
                      {selectedEffect?.id === effect.id ? 'Selected' : 'Select'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Selection Details */}
        {selectedEffect && (
          <div style={{ 
            padding: '15px 20px',
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #ddd',
            borderBottom: '1px solid #ddd'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: getEffectVariants(selectedEffect).length > 0 ? '15px' : '0' }}>
              <div style={{ fontWeight: '600' }}>
                Selected: {selectedEffect.name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontWeight: '600' }}>Quantity:</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  style={{
                    width: '70px',
                    padding: '5px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}
                />
              </div>
            </div>
            
            {/* Variant Selection */}
            {getEffectVariants(selectedEffect).length > 0 && (
              <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                <div style={{ marginBottom: '10px', fontWeight: '600', fontSize: '14px' }}>
                  Select Variant:
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                  {getEffectVariants(selectedEffect).map((variant, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedVariant(variant.name)}
                      style={{
                        padding: '10px',
                        border: `2px solid ${selectedVariant === variant.name ? '#3b82f6' : '#e5e7eb'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        backgroundColor: selectedVariant === variant.name ? '#eff6ff' : 'white',
                        transition: 'all 0.2s',
                        fontSize: '14px'
                      }}
                    >
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                        {variant.name}
                      </div>
                      {variant.gas_type && (
                        <div style={{ color: '#6b7280', fontSize: '12px' }}>
                          Config: {variant.gas_type}
                        </div>
                      )}
                      {variant.range > 0 && (
                        <div style={{ color: '#6b7280', fontSize: '12px' }}>
                          Range: {variant.range}m
                        </div>
                      )}
                      {variant.clearance_height > 0 && (
                        <div style={{ color: '#6b7280', fontSize: '12px' }}>
                          Clearance: {variant.clearance_height}m
                        </div>
                      )}
                      {variant.description && (
                        <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                          {variant.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '0 20px 20px',
          paddingTop: '20px'
        }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {selectedEffect && (
            <button 
              className="btn btn-primary" 
              onClick={handleConfirmSelection}
              style={{ marginLeft: '10px' }}
            >
              Add Effect{selectedVariant ? ` - ${selectedVariant}` : ''} (Qty: {quantity})
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EffectSelectionModal;